// Documents (workflow package C, preview-v3 mockup): the library of
// published scripts and call sheets held by the two sibling tools, grouped
// by project. A row opens that document's admin view inside the OS through
// the ToolEmbed deep link. The whole pool is fetched once; filtering,
// sorting, counting and grouping live in lib/documentsLibrary.ts.

import { DocumentsDashboardUpload } from '../components/DocumentsDashboardUpload';
import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useIsMobile } from '../lib/useIsMobile';
import { useViewState } from '../lib/useViewState';
import { bool, encodeState, getSessionStorage, nullableStr, oneOf, str, writeNavScroll, writeNavSearch } from '../lib/viewState';
import { melbourneToday } from '../../shared/dates';
import {
  Dropdown,
  FilterBar,
  formatDayMonth,
  Group,
  GroupLink,
  PageHeader,
  Pill,
  SearchBox,
  SkeletonRows,
  SortButton,
  Tabs,
  VitalsSlim,
  VS,
  type SortValue,
} from '../components/primitives';
import { Sheet, SheetButton, SheetRow } from '../components/Sheet';
import { FilterSheet } from '../components/FilterSheet';
import { CallSheetsIcon, MoreIcon, FilterIcon, ScriptsIcon, SearchIcon } from '../components/icons';
import {
  cachedDocuments,
  clientItems,
  DEFAULT_DOC_SORT,
  DOC_SORTS,
  DOC_TABS,
  documentCounts,
  documentHref,
  documentViews,
  type DocumentView,
  documentMetaLine,
  documentStatus,
  documentVitals,
  groupDocuments,
  outageMessage,
  parseDocSort,
  projectItems,
  rememberDocuments,
  serializeDocSort,
  STATUS_ITEMS,
  STATUS_LABEL,
  STATUS_TONE,
  visibleDocuments,
  type DocumentsTab,
} from '../lib/documentsLibrary';
import type { Document, DocumentsResponse } from '../../shared/types';

export const documentsViewCodec = {
  tab: oneOf<DocumentsTab>('view', ['all', 'script', 'callsheet'], 'all'),
  query: str('q'),
  client: nullableStr('client'),
  project: nullableStr('project'),
  status: nullableStr('status'),
  // null means the default (updated, newest first).
  sort: { ...nullableStr('sort'), dec: (raw: string) => {
    const parsed = parseDocSort(raw);
    return parsed && serializeDocSort(parsed) === raw ? raw : undefined;
  } },
  drafts: bool('drafts'),
};

type FilterKeys = { client: string | null; project: string | null; status: string | null };
const NO_FILTERS: FilterKeys = { client: null, project: null, status: null };

/** Committed library view — what the URL and back-navigation carry. */
export interface DocumentsView extends FilterKeys {
  tab: DocumentsTab;
  query: string;
  sort: SortValue;
  drafts: boolean;
}

export interface DocumentsBodyProps {
  mobile: boolean;
  /** null until the first response lands. */
  data: DocumentsResponse | null;
  loaded: boolean;
  /** The request itself failed (not a per-source outage). */
  failed: boolean;
  view: DocumentsView;
  onView: (patch: Partial<DocumentsView>) => void;
  onOpen: (d: Document, view?: DocumentView) => void;
  onRetry: () => void;
  today: string;
}

// Session expiry drops the instant-paint cache along with the session.
if (typeof window !== 'undefined') window.addEventListener('ss:unauthorized', () => rememberDocuments(null));

const DocIcon = ({ tool }: { tool: Document['tool'] }) => (
  <span className="row-icon">{tool === 'script' ? <ScriptsIcon /> : <CallSheetsIcon />}</span>
);

const StatusPill = ({ doc }: { doc: Document }) => {
  const status = documentStatus(doc);
  return <Pill tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Pill>;
};

function DocumentViewMenu({ doc, mobile, onOpen }: { doc: Document; mobile: boolean; onOpen: DocumentsBodyProps['onOpen'] }) {
  const [open, setOpen] = useState(false);
  const items = documentViews(doc);
  return <span className="dropdown-anchor docs-view-menu" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
    <button className="docs-view-button" type="button" aria-label={`Views for ${doc.title}`} aria-haspopup={mobile ? 'dialog' : 'menu'} aria-expanded={open}
      disabled={!documentHref(doc)} onClick={() => setOpen((v) => !v)}><MoreIcon /></button>
    {mobile ? <Sheet title="Open document" variant="half" open={open} onClose={() => setOpen(false)}
      rightAction={<SheetButton onClick={() => setOpen(false)}>Close</SheetButton>}>
      {items.map((v) => <SheetRow key={v.value} label={v.label} role="menuitem" onSelect={() => { setOpen(false); onOpen(doc, v.value); }} />)}
    </Sheet> : open && <Dropdown keyboardNavigation label="Open document" items={items} onClose={() => setOpen(false)} onSelect={(view) => { setOpen(false); onOpen(doc, view); }} />}
  </span>;
}

/** Presentational body — props in, markup out; Documents owns fetch + URL. */
export function DocumentsBody({ mobile, data, loaded, failed, view, onView, onOpen, onRetry, today }: DocumentsBodyProps) {
  // Phone sheet drafts — never published.
  const [draft, setDraft] = useState<FilterKeys>(NO_FILTERS);
  const [draftSort, setDraftSort] = useState(view.sort.key);
  const [draftDrafts, setDraftDrafts] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(() => !!view.query);

  const pool = data?.documents ?? [];
  const visible = visibleDocuments(pool, view, view.sort);
  const groups = groupDocuments(visible);
  const counts = documentCounts(pool, view.drafts);
  const { inReview, updatedThisWeek } = documentVitals(pool, today, view.drafts);
  const clients = clientItems(pool);
  const projects = projectItems(data?.projects ?? []);
  const outage = failed ? 'Documents couldn’t be reached.' : data ? outageMessage(data.sources, data.documents) : null;
  const allDown = failed || (data !== null && !data.sources.scripts.ok && !data.sources.callsheets.ok);
  const liveLabel = allDown ? 'Unavailable' : data?.partial ? 'Partial results' : undefined;
  const loading = !loaded;
  const libraryEmpty = pool.length === 0;
  const activeFilterCount = [view.client, view.project, view.status].filter(Boolean).length + (view.drafts ? 1 : 0);
  const clearAll = () => onView({ query: '', ...NO_FILTERS, tab: 'all' });

  const outageNotice = outage && (
    <div className="docs-notice" role="status">
      <span className="dot" />{outage}
      <button type="button" className="btn" onClick={onRetry}>Retry</button>
    </div>
  );
  // Nothing to say under an outage that left no documents; the notice speaks.
  const emptyText = allDown || (libraryEmpty && data?.partial)
    ? null
    : libraryEmpty
      ? 'No published documents yet — scripts and call sheets you publish in their tools appear here.'
      : 'No documents match.';
  const sortLabel = DOC_SORTS.find((s) => s.key === view.sort.key)?.label;

  if (mobile) {
    const sheetCount = visibleDocuments(pool, { ...view, ...draft, drafts: draftDrafts }, view.sort).length;
    return (
      <>
        <PageHeader title="Documents" count={visible.length} liveLabel={liveLabel} mobileActions={[
          { key: 'search', label: 'Search documents', icon: <SearchIcon />, onPress: () => setSearchOpen((s) => !s), active: searchOpen },
          { key: 'filter', label: 'Filter & sort', icon: <FilterIcon />, onPress: () => {
            setDraft({ client: view.client, project: view.project, status: view.status });
            setDraftSort(view.sort.key);
            setDraftDrafts(view.drafts);
            setFilterOpen(true);
          }, active: activeFilterCount > 0 },
        ]} />
        <DocumentsDashboardUpload />
        <div className="mtabs">
          {DOC_TABS.map((t) => (
            <button key={t.value} type="button" className={`t${view.tab === t.value ? ' on' : ''}`} aria-pressed={view.tab === t.value} onClick={() => onView({ tab: t.value })}>
              {t.label}<span className="c">{counts[t.value]}</span>
            </button>
          ))}
        </div>
        {searchOpen && <div className="docs-m-search"><SearchBox placeholder="Search documents, clients…" value={view.query} onChange={(query) => onView({ query })} /></div>}
        <div className="project-list-vitals"><span className="accent">{inReview} in review</span><span>Sort · {sortLabel} {view.sort.dir === 'asc' ? '↑' : '↓'}</span></div>
        <div className="page-body project-list-body"><div className="project-list-content">
          {outageNotice}
          {loading ? <SkeletonRows rows={6} /> : groups.map((g) => (
            <section key={g.key}>
              <div className="project-list-group">
                <span>{g.title}</span>
                <span className="project-list-group-count">{g.docs.length}</span>
              </div>
              {g.docs.map((d) => {
                const inner = (
                  <>
                    <DocIcon tool={d.tool} />
                    <span className="prow-main">
                      <span className="prow-title">{d.title}</span>
                      <span className="prow-meta">{documentMetaLine(d)} · upd {formatDayMonth(d.updated_date)}</span>
                      <StatusPill doc={d} />
                    </span>
                  </>
                );
                return (
                  <div key={d.key} className="project-list-row compact docs-mobile-row">
                    {documentHref(d) ? (
                      <button type="button" className="project-list-open" onClick={() => onOpen(d, 'editor')} aria-label={`Open ${d.title}`}>{inner}</button>
                    ) : (
                      <div className="project-list-open" aria-disabled="true">{inner}</div>
                    )}
                    <DocumentViewMenu doc={d} mobile onOpen={onOpen} />
                  </div>
                );
              })}
            </section>
          ))}
          {!loading && !visible.length && emptyText && <div className="project-list-empty" style={{ padding: '24px 20px' }}>{emptyText}</div>}
        </div></div>
        <FilterSheet open={filterOpen} onClose={() => setFilterOpen(false)} resultLabel={`Show ${sheetCount} ${sheetCount === 1 ? 'document' : 'documents'}`}
          onApply={() => {
            const opt = DOC_SORTS.find((s) => s.key === draftSort) ?? DOC_SORTS[0];
            onView({
              ...draft,
              sort: opt.key === view.sort.key ? view.sort : { key: opt.key, dir: opt.natural },
              drafts: draftDrafts,
            });
            setFilterOpen(false);
          }}
          onClear={() => { setDraft(NO_FILTERS); setDraftSort(DEFAULT_DOC_SORT.key); setDraftDrafts(false); }}
          sections={[
            { key: 'client', label: 'Client', kind: 'picker', items: clients, value: draft.client, placeholder: 'All clients', onChange: (v) => setDraft({ ...draft, client: v }) },
            { key: 'project', label: 'Project', kind: 'picker', items: projects, value: draft.project, placeholder: 'All projects', onChange: (v) => setDraft({ ...draft, project: v }) },
            { key: 'status', label: 'Status', kind: 'chips', options: STATUS_ITEMS, value: draft.status ?? '', onChange: (v: string) => setDraft({ ...draft, status: v === draft.status ? null : v }) },
            { key: 'sort', label: 'Sort', kind: 'chips', options: DOC_SORTS.map((s) => ({ value: s.key, label: `${s.label} · ${s.naturalLabel}` })), value: draftSort, onChange: setDraftSort },
            { key: 'drafts', label: 'Include drafts', kind: 'toggle', value: draftDrafts, onChange: setDraftDrafts },
          ]} />
      </>
    );
  }

  return (
    <>
      <PageHeader title="Documents" count={visible.length} liveLabel={liveLabel} />
      <DocumentsDashboardUpload />
      <div className="toolbar">
        <Tabs options={DOC_TABS} value={view.tab} onChange={(tab) => onView({ tab })} />
        <FilterBar fields={[
          { key: 'client', label: 'Client', items: clients, searchable: true },
          { key: 'project', label: 'Project', items: projects, searchable: true },
          { key: 'status', label: 'Status', items: STATUS_ITEMS },
        ]} active={{ client: view.client, project: view.project, status: view.status }}
          onChange={(k, v) => onView({ [k]: v } as Partial<FilterKeys>)} />
        <SortButton options={DOC_SORTS} value={view.sort} onChange={(sort) => onView({ sort })} />
        <SearchBox placeholder="Search documents, clients…" value={view.query} onChange={(query) => onView({ query })} />
        <button type="button" className={`btn${view.drafts ? ' on' : ''}`} aria-pressed={view.drafts} onClick={() => onView({ drafts: !view.drafts })}>
          {view.drafts ? 'Hide drafts' : 'Include drafts'}
        </button>
      </div>
      <div className="page-body"><div className="content wide">
        <VitalsSlim>
          <VS label="Scripts" value={counts.script} tone="accent" />
          <VS label="Call sheets" value={counts.callsheet} tone="accent" />
          <VS label="In review" value={inReview} tone="gold" />
          <VS label="Updated this week" value={updatedThisWeek} />
        </VitalsSlim>
        {outageNotice}
        {loading ? <SkeletonRows rows={6} /> : groups.map((g) => (
          <Group key={g.key} title={g.title} count={g.docs.length}
            link={g.projectId ? <GroupLink to={`/projects/${g.projectId}`}>{g.clientName ? `${g.clientName} · ` : ''}Project →</GroupLink> : undefined}>
            {g.docs.map((d) => {
              const line = documentMetaLine(d);
              const openable = documentHref(d) !== null;
              return (
                <div key={d.key} className="row docs-row"
                  {...(openable
                    ? {
                        role: 'link',
                        tabIndex: 0,
                        onClick: () => onOpen(d, 'editor'),
                        onKeyDown: (e: KeyboardEvent) => { if (e.key === 'Enter') onOpen(d, 'editor'); },
                      }
                    : { 'aria-disabled': true })}>
                  <DocIcon tool={d.tool} />
                  <span className="row-title" title={d.title}>{d.title}</span>
                  <span className="row-context" title={line}>{line}</span>
                  <span className="row-spacer" />
                  <StatusPill doc={d} />
                  <span className="row-meta dim col-updated">upd {formatDayMonth(d.updated_date)}</span>
                  <span className="col-view">{openable ? 'Open ↗' : ''}</span>
                  <DocumentViewMenu doc={d} mobile={false} onOpen={onOpen} />
                </div>
              );
            })}
          </Group>
        ))}
        {!loading && !visible.length && emptyText && (
          <div className="empty-note">{emptyText}{!libraryEmpty && <> <button type="button" className="group-link" onClick={clearAll}>Clear filters</button></>}</div>
        )}
      </div></div>
    </>
  );
}

export default function Documents() {
  const { initial, publish } = useViewState(documentsViewCodec, (state) => {
    setTab(state.tab);
    setQuery(state.query);
    setFilters({ client: state.client, project: state.project, status: state.status });
    setSort(parseDocSort(state.sort) ?? DEFAULT_DOC_SORT);
    setDrafts(state.drafts);
  });
  const mobile = useIsMobile();
  const navigate = useNavigate();
  const [tab, setTab] = useState<DocumentsTab>(() => initial.tab);
  const [query, setQuery] = useState(() => initial.query);
  const [filters, setFilters] = useState<FilterKeys>(() => ({ client: initial.client, project: initial.project, status: initial.status }));
  const [sort, setSort] = useState<SortValue>(() => parseDocSort(initial.sort) ?? DEFAULT_DOC_SORT);
  const [drafts, setDrafts] = useState(() => initial.drafts);
  // A cached response paints at once; the fetch below replaces it.
  const [data, setData] = useState<DocumentsResponse | null>(cachedDocuments);
  const [loaded, setLoaded] = useState(() => cachedDocuments() !== null);
  const [failed, setFailed] = useState(false);
  const listSeq = useRef(0);
  const mutatedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    publish({
      tab,
      query,
      client: filters.client,
      project: filters.project,
      status: filters.status,
      sort: serializeDocSort(sort) === serializeDocSort(DEFAULT_DOC_SORT) ? null : serializeDocSort(sort),
      drafts,
    });
  }, [publish, tab, query, filters.client, filters.project, filters.status, sort, drafts]);

  const fetchDocuments = useCallback(() => {
    const seq = ++listSeq.current;
    api<DocumentsResponse>('/api/admin/documents')
      .then((d) => { rememberDocuments(d); if (seq === listSeq.current) { setData(d); setFailed(false); } })
      .catch(() => { if (seq === listSeq.current) setFailed(true); })
      .finally(() => { if (seq === listSeq.current) setLoaded(true); });
  }, []);
  useEffect(() => {
    fetchDocuments();
    const onMutated = () => {
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      mutatedTimer.current = setTimeout(fetchDocuments, 250);
    };
    window.addEventListener('ss:mutated', onMutated);
    return () => {
      window.removeEventListener('ss:mutated', onMutated);
      if (mutatedTimer.current) clearTimeout(mutatedTimer.current);
      ++listSeq.current;
    };
  }, [fetchDocuments]);

  const onView = (patch: Partial<DocumentsView>) => {
    if (patch.tab !== undefined) setTab(patch.tab);
    if (patch.query !== undefined) setQuery(patch.query);
    if (patch.sort !== undefined) setSort(patch.sort);
    if (patch.drafts !== undefined) setDrafts(patch.drafts);
    if ('client' in patch || 'project' in patch || 'status' in patch) {
      setFilters((f) => ({
        client: 'client' in patch ? patch.client ?? null : f.client,
        project: 'project' in patch ? patch.project ?? null : f.project,
        status: 'status' in patch ? patch.status ?? null : f.status,
      }));
    }
  };
  const onOpen = (d: Document, view: DocumentView = 'editor') => {
    // Snapshot committed state synchronously, including a just-typed search;
    // a pending replaceState/scroll animation frame must not lose the return.
    const search = new URLSearchParams(encodeState(documentsViewCodec, {
      tab, query, ...filters, sort: serializeDocSort(sort) === serializeDocSort(DEFAULT_DOC_SORT) ? null : serializeDocSort(sort), drafts,
    })).toString();
    const returnTo = `/documents${search ? `?${search}` : ''}`;
    const href = documentHref(d, view, returnTo);
    if (!href) return;
    const storage = getSessionStorage();
    writeNavSearch(storage, '/documents', search ? `?${search}` : '');
    writeNavScroll(storage, '/documents', document.querySelector('.page-body')?.scrollTop ?? 0);
    navigate(href);
  };

  return (
    <DocumentsBody
      mobile={mobile}
      data={data}
      loaded={loaded}
      failed={failed}
      view={{ tab, query, ...filters, sort, drafts }}
      onView={onView}
      onOpen={onOpen}
      onRetry={fetchDocuments}
      today={melbourneToday()}
    />
  );
}
